import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router'
import { toast } from 'sonner'
import * as Sentry from '@sentry/react'
import { trpc } from 'shared/api'
import { LANGUAGE_NAMES, useTranslate } from 'shared/lib/i18n'
import { useAuthStore, type User } from 'shared/store/auth'

import { Button } from 'shared/components/ui/button'
import { Input } from 'shared/components/ui/input'
import {
	Field,
	FieldGroup,
	FieldLabel,
	FieldError,
} from 'shared/components/ui/field'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuTrigger,
} from 'shared/components/ui/dropdown-menu'
import { Flag } from 'shared/components/flag'
import { LanguageRadioItems } from 'shared/components/language-choice'
import { useLanguage } from 'shared/components/use-language'
import { GalleryVerticalEnd, Globe, Settings } from 'lucide-react'

export function Login() {
	const navigate = useNavigate()
	const { t } = useTranslate()
	const { lang } = useLanguage()

	const setAuth = useAuthStore((state) => state.setAuth)

	const [username, setUsername] = useState('')
	const [password, setPassword] = useState('')
	const [error, setError] = useState<string | null>(null)

	const loginMutation = useMutation({
		...trpc.auth.Login.mutationOptions(),
		onSuccess: (data) => {
			// Type assertion needed due to cross-project type inference limitations
			const { token, user } = data as { token: string; user: User }
			setAuth(token, user)
			toast.success(t('login.success'))
			navigate('/', { replace: true })
		},
		onError: (err) => {
			// The server's words are English for the log; the person reads the panel's own.
			const message =
				err.data?.code === 'UNAUTHORIZED'
					? t('login.invalid')
					: t('login.failed', { message: err.message })
			setError(message)
			toast.error(message)
			Sentry.captureException(err)
		},
	})

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault()
		setError(null)
		loginMutation.mutate({ username, password })
	}

	return (
		<div className='grid min-h-svh lg:grid-cols-2'>
			<div className='flex flex-col gap-4 p-6 md:p-10'>
				<div className='flex items-center justify-between gap-2'>
					<a href='#' className='flex items-center gap-2 font-medium'>
						<div className='bg-primary text-primary-foreground flex size-6 items-center justify-center rounded-md'>
							<GalleryVerticalEnd className='size-4' />
						</div>
						TezGo
					</a>
					<div className='flex items-center gap-1'>
						{/* The language picker names the language itself, so it is found in any. */}
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button variant='ghost' size='sm' title={t('settings.language')} data-testid='login-language'>
									<Globe />
									<Flag lang={lang} className='size-auto h-3.5 rounded-xs' />
									{LANGUAGE_NAMES[lang]}
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align='end' className='w-auto'>
								<LanguageRadioItems />
							</DropdownMenuContent>
						</DropdownMenu>
						<Button variant='ghost' size='icon-sm' asChild>
							<Link to='/settings' title={t('settings.title')} aria-label={t('settings.title')} data-testid='login-settings'>
								<Settings />
							</Link>
						</Button>
					</div>
				</div>
				<div className='flex flex-1 items-center justify-center'>
					<div className='w-full max-w-xs'>
						<form
							onSubmit={handleSubmit}
							className='flex flex-col gap-6'
							data-testid='login-form'
						>
							<FieldGroup>
								<div className='flex flex-col items-center gap-1 text-center'>
									<h1 className='text-2xl font-bold'>{t('login.title')}</h1>
									<p className='text-muted-foreground text-sm text-balance'>
										{t('login.subtitle')}
									</p>
								</div>

								<Field>
									<FieldLabel htmlFor='username'>{t('login.username')}</FieldLabel>
									<Input
										id='username'
										type='text'
										placeholder={t('login.usernamePlaceholder')}
										value={username}
										onChange={(e) => setUsername(e.target.value)}
										required
										autoComplete='username'
									/>
								</Field>

								<Field>
									<FieldLabel htmlFor='password'>{t('login.password')}</FieldLabel>
									<Input
										id='password'
										type='password'
										placeholder={t('login.passwordPlaceholder')}
										value={password}
										onChange={(e) => setPassword(e.target.value)}
										required
										autoComplete='current-password'
									/>
								</Field>

								{error && (
									<FieldError data-testid='login-error'>{error}</FieldError>
								)}

								<Field>
									<Button
										type='submit'
										disabled={loginMutation.isPending}
										className='w-full'
										data-testid='login-submit'
									>
										{loginMutation.isPending ? t('login.submitting') : t('login.submit')}
									</Button>
								</Field>
							</FieldGroup>
						</form>
					</div>
				</div>
			</div>
			<div className='bg-muted relative hidden lg:block'>
				<div className='absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary/20 to-primary/5'>
					<div className='text-center p-8'>
						<GalleryVerticalEnd className='size-24 mx-auto mb-4 text-primary/30' />
						<h2 className='text-3xl font-bold text-primary/50'>TezGo</h2>
						<p className='text-primary/40 mt-2'>{t('login.panel')}</p>
					</div>
				</div>
			</div>
		</div>
	)
}
